/**
 * TanStack Query hooks for `aws-s3-export-scheduled-click-conversion-logs`.
 * Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	list,
	patchSchedule,
	remove,
	runNow,
	awsS3ExportScheduledClickConversionLogsQueryKeys,
	type CreateBody,
	type ListResponse,
	type PatchBody,
	type ScheduleRow,
} from "./api";

export function useAwsS3ExportScheduledClickConversionLogsList(): UseQueryResult<
	ListResponse,
	Error
> {
	return useQuery({
		queryKey: awsS3ExportScheduledClickConversionLogsQueryKeys.list(),
		queryFn: list,
	});
}

export function useAwsS3ExportScheduledClickConversionLogsCreate(): UseMutationResult<
	{ item: ScheduleRow },
	Error,
	CreateBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: awsS3ExportScheduledClickConversionLogsQueryKeys.all,
			});
		},
	});
}

export function useAwsS3ExportScheduledClickConversionLogsPatch(): UseMutationResult<
	{ item: ScheduleRow },
	Error,
	{ id: string; body: PatchBody }
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ id, body }) => patchSchedule(id, body),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: awsS3ExportScheduledClickConversionLogsQueryKeys.all,
			});
		},
	});
}

export function useAwsS3ExportScheduledClickConversionLogsRunNow(): UseMutationResult<
	{ item: ScheduleRow },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: runNow,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: awsS3ExportScheduledClickConversionLogsQueryKeys.all,
			});
		},
	});
}

export function useAwsS3ExportScheduledClickConversionLogsDelete(): UseMutationResult<
	{ ok: boolean; id: string },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: awsS3ExportScheduledClickConversionLogsQueryKeys.all,
			});
		},
	});
}
